/**
 * Putting a stored training program back onto the plan, and parking what the user was
 * training so it can be put back on top.
 *
 * This lives in its own file rather than in `exercise-switching.ts`, where it used to sit,
 * for one reason: nothing here needs the exercise database, so nothing here needs Angular.
 * `exercise-switching.ts` imports values from `services/exercise-db.service`, which drags
 * the whole Angular injector in at module load and puts these rules out of reach of the
 * mocha unit runner. `exercise-switching.ts` re-exports everything below, so existing call
 * sites are unchanged. See `program-backup-restore.test.ts` (#5113).
 */

import { DateTime } from 'luxon';
import { FitnessPlan, Plan_SubSlot, TrainingProgramBackup, coachChangeStampOf, copySubSlotTrainingStateForRestore, findOldSubslot } from '@cf-shared/data-model/fitness-plan';
import { FitnessState } from '@cf-shared/data-model/user-state';
import { deepClone, deepOverwrite } from '@cf-shared/deep-helpers';
import { reducedTargetWeightAfterDays } from '@cf-shared/fitness-constants';
import { daysSinceDate } from 'util/time';

/// Store the current exercise in the backup list, if it was used before
export function backupUsedSubSlot(subSlotId: string, plan: FitnessPlan, fitnessState: FitnessState) {
  let currentSlot = plan.subSlots[subSlotId];
  // if the exercise wasn't even used yet
  if (!currentSlot || !currentSlot.lastTimeConcluded_utc) {
    // do nothing
    return;
  }

  // if the exercise was already backed up
  let existingSubslot = findOldSubslot(fitnessState, subSlotId, currentSlot.exercise_id)
  if (existingSubslot) {
    // use that data
    deepOverwrite(existingSubslot, currentSlot);
    // if this is the first time it was switched away
  } else {
    // generate its backup
    if (!fitnessState.oldSubslots) {
      fitnessState.oldSubslots = [];
    }
    fitnessState.oldSubslots.push(deepClone(currentSlot));
  }
}

function restoreSlotFromBackup(newSlot: Plan_SubSlot, slotBackup: Plan_SubSlot) {
  deepOverwrite(newSlot, slotBackup);
}

/**
 * How much of the slot the user was training goes back onto a slot restored from a program
 * backup.
 *
 * - `wholeSlot` copies the live slot over the restored one entirely, set count included.
 *   This is what plan RECOVERY and the exercise switcher want: there the live slot is the
 *   best record of the exercise there is, and everything on it is worth keeping.
 * - `trainingStateOnly` copies only what training itself wrote, leaving the backup's own
 *   `defaultVolume_sets` in place. This is what the Edit Workouts "Restore Original Plan"
 *   button wants: a hand-edited set count is precisely the change the user asked to have
 *   undone, while their earned progression is not (#5113).
 */
export type RestoredSlotCarry = 'wholeSlot' | 'trainingStateOnly';

// if the new exercise already has a subslot used _recently enough_ (not enough is if it would have caused a re-test anyway)
export function restoreSlotFromBackupIfValid(fitnessState: FitnessState, oldSubslotId: string, newExerciseId: string, todayLocal: DateTime, newSubslot: Plan_SubSlot, carry: RestoredSlotCarry = 'wholeSlot') {
  let slotBackup = findOldSubslot(fitnessState, oldSubslotId, newExerciseId);
  let reduction = slotBackup && reducedTargetWeightAfterDays(daysSinceDate(todayLocal, slotBackup.lastTimeConcluded_utc));
  if (slotBackup && reduction !== 'retest') {
    // clone that one
    if (carry === 'trainingStateOnly') {
      copySubSlotTrainingStateForRestore(newSubslot, slotBackup);
    } else {
      restoreSlotFromBackup(newSubslot, slotBackup);
    }
  }
}

/**
 * Restore a backup of the training program.
 *
 * The backup holds the program as the build left it, training state and all (see
 * `TrainingProgramBackup`). So this parks every live slot in `fitnessState.oldSubslots`,
 * clones the backup over the plan, and then puts the live state back on each restored slot
 * whose slot, offset and exercise all still match — skipping one last trained more than
 * three weeks ago, which the layoff rule would strength-retest anyway.
 *
 * @param carry how much of the live slot goes back on. Defaults to `wholeSlot`, which is
 *   what plan recovery has always done; the Edit Workouts restore passes
 *   `trainingStateOnly` so a hand-edited set count is undone. See {@link RestoredSlotCarry}.
 */
export function restoreTrainingProgram(plan: FitnessPlan, fitnessState: FitnessState, backup: TrainingProgramBackup, todayLocal: DateTime, carry: RestoredSlotCarry = 'wholeSlot') {

  // store all existing subslots into oldSubslots
  if (!fitnessState.oldSubslots) {
    fitnessState.oldSubslots = [];
  }
  for (let subslotId of Object.keys(plan.subSlots)) {
    backupUsedSubSlot(subslotId, plan, fitnessState);
  }

  // #6428: remember each slot's coach stamp before the wipe. A slot never trained is not parked
  // above at all, and a slot past the layoff limit is not put back, so neither branch below would
  // carry it; without it the client's app would report a coach change as overwritten
  const coachStamps = new Map<string, { exerciseId: string; stamp: string }>();
  for (const [subslotId, subSlot] of Object.entries(plan.subSlots)) {
    const stamp = coachChangeStampOf(subSlot);
    if (stamp) coachStamps.set(subslotId, { exerciseId: subSlot.exercise_id, stamp });
  }

  // remove all existing subslots
  plan.subSlots = {};

  // for each subslot in the backup
  for (let subslotId of Object.keys(backup.subSlots)) {
    // copy the subslot from backup (this holds the program as the build left it)
    plan.subSlots[subslotId] = deepClone(backup.subSlots[subslotId]);

    // restore any progress data if possible
    restoreSlotFromBackupIfValid(fitnessState, subslotId, plan.subSlots[subslotId].exercise_id, todayLocal, plan.subSlots[subslotId], carry);

    // put the coach stamp back on a slot that still holds the same exercise, in every branch above
    const remembered = coachStamps.get(subslotId);
    if (remembered && remembered.exerciseId === plan.subSlots[subslotId].exercise_id) {
      plan.subSlots[subslotId].coachChange_utc = remembered.stamp;
    }
  }

  // copy the workouts
  plan.workouts = deepClone(backup.workouts);
}
